import { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { CheckCircle2 } from 'lucide-react';
import { useSchoolConfirmationWithForm } from '@/domains/school-requests/hooks/useSchoolConfirmationWithForm';
import { useEditions } from '@/shared/hooks/use-editions';
import { useAuthProviderV2 } from '@/providers/provider-hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { DOCUMENT_LINKS } from '@/constants/config';
import { useEditionDocuments } from '@/shared/hooks/use-edition-documents';
import { RefreshCw } from 'lucide-react';

/**
 * Profesorul care revine, dupa rotirea editiei: doua tap-uri (bifa regulamentul, „Confirm
 * participarea"), fara recompletarea formularului de inscriere. Datele scolii se preiau din
 * cererea aprobata a editiei trecute (backend: getSchoolRequestForEditionReRegistration).
 */
export default function SchoolRegistrationConfirmationPage() {
  const { form, confirm, decline, isSubmitting } = useSchoolConfirmationWithForm();
  // `isError`/`retry` erau deja expuse de `useEditions` (precedentul silent-500 al
  // acestui fisier), dar acest consumator ignora ambele — un 500 pe editii cadea tacut
  // pe eticheta genericA "editia noua", ca si cum totul ar fi normal, in loc sa spuna
  // ca datele editiei n-au putut fi incarcate.
  const { currentEdition, isLoading: editionsLoading, isError: editionsError, retry: retryEditions } = useEditions();
  const { logout } = useAuthProviderV2();
  // SE-14: regulamentul vine din backend (editia curenta), cu fallback pe DOCUMENT_LINKS.
  const documente = useEditionDocuments();
  const editionName = currentEdition?.nume ?? 'ediția nouă';

  // Un tap ratat pe "Nu doresc sa fiu coordonator" desface legatura profesor-scoala (backend:
  // SchoolRequestService#243-268). Dialogul recupereaza semantica de alertdialog manual, pentru
  // ca alert-dialog.tsx nu exista in proiect si nu se adauga dependinte noi.
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const handleConfirmDecline = () => {
    setIsDeclineDialogOpen(false);
    decline();
  };

  return (
    <div className="flex min-h-dvh items-center justify-center px-0 py-6 sm:p-4">
      {/* La 360px cardul e edge-to-edge, ca sa ramana 16px pana la continut (erau ~41px). */}
      <Card className="w-full max-w-md rounded-none border-x-0 sm:rounded-xl sm:border-x">
        <CardHeader className="px-4 text-center">
          <CheckCircle2 className="mx-auto mb-1 h-8 w-8 text-magenta" aria-hidden />
          <CardTitle className="text-fluid-xl">Bine ați revenit în Școala Ecoterrienilor!</CardTitle>
        </CardHeader>

        <Form {...form}>
          <form onSubmit={confirm} noValidate>
            <CardContent className="space-y-4 px-4">
              {/* Cosmetic, nu blocant: numele editiei nu conditioneaza confirm/decline, dar
                  un 500 tacut aici arata identic cu „totul e normal" - un semnal minim spune
                  ca a esuat ceva, cu reincercare directa. */}
              {editionsError && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-warning bg-warning/10 p-3 text-fluid-base text-warning">
                  <span>Nu am putut încărca numele ediției curente.</span>
                  <button
                    type="button"
                    onClick={() => retryEditions()}
                    className="touch-target flex shrink-0 items-center gap-1 underline"
                  >
                    <RefreshCw className="size-4" aria-hidden /> Reîncearcă
                  </button>
                </div>
              )}
              {/* Banner-invitatie, nu alerta: lilac, nu tokenul de info (albastru). Eticheta
                  editiei e cea masurata de S1 cu stub intarziat 9s (p2-4): pana se rezolva
                  interogarea, `aria-busy` + `<Spinner>` anunta explicit asistiv, nu doar
                  vizual - `<Spinner>` are deja `role="status"` incorporat, consumat nu
                  modificat. */}
              <div className="rounded-lg border border-lilac bg-lilac/30 p-3 text-center" aria-busy={editionsLoading}>
                {editionsLoading ? (
                  <p className="flex items-center justify-center gap-2 font-semibold text-(--color-dark)">
                    <Spinner className="size-4" /> Se încarcă...
                  </p>
                ) : (
                  <p className="font-semibold text-(--color-dark)">A început {editionName}</p>
                )}
                <p className="text-fluid-base text-(--color-dark)/80">
                  Dacă doriți ca școala dumneavoastră să se reînscrie în ediție, confirmați mai jos.
                </p>
              </div>

              <FormField
                control={form.control}
                name="amCititSiSuntDeAcord"
                render={({ field }) => (
                  <FormItem>
                    <label className="touch-target -mx-2 flex w-full cursor-pointer items-start gap-3 rounded-md px-2 py-1 text-[length:var(--text-medium)] transition-colors hover:bg-lilac/20 active:bg-lilac/30">
                      <Controller
                        control={form.control}
                        name="amCititSiSuntDeAcord"
                        render={({ field: cb }) => (
                          <Checkbox
                            className="border-magenta"
                            checked={cb.value}
                            onCheckedChange={(v) => cb.onChange(v === true)}
                            disabled={isSubmitting}
                            aria-describedby="acord-descriere"
                          />
                        )}
                      />
                      <span id="acord-descriere">
                        Am citit și sunt de acord cu{' '}
                        <a
                          href={DOCUMENT_LINKS.NOTA_INFORMARE_PROFESORI}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-magenta underline hover:text-magenta-hover"
                        >
                          Nota de informare
                        </a>{' '}
                        și{' '}
                        <a
                          href={documente.regulament}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-magenta underline hover:text-magenta-hover"
                        >
                          Regulamentul campaniei
                        </a>
                        .
                      </span>
                    </label>
                    <FormMessage />
                    <span className="sr-only">{field.value ? 'bifat' : 'nebifat'}</span>
                  </FormItem>
                )}
              />
            </CardContent>

            <CardFooter className="flex flex-col gap-3 px-4">
              <Button type="submit" className="touch-target w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Se procesează...' : `Reînscriu școala în ${editionName}`}
              </Button>
              <div className="flex w-full items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-medium text-dark/70">sau</span>
                <Separator className="flex-1" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="touch-target w-full"
                disabled={isSubmitting}
                onClick={() => setIsDeclineDialogOpen(true)}
              >
                Nu doresc să fiu coordonator în această ediție
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={logout}
                disabled={isSubmitting}
                className="touch-target h-auto p-0 text-dark/70 underline"
              >
                {/* text-medium pe span, nu pe Button: cn()/tailwind-merge clasifica text-medium
                    drept culoare (nu e in scara de font-size cunoscuta) si il elimina in
                    conflict cu text-dark/70. */}
                <span className="text-medium">Deconectare</span>
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>

      <Dialog open={isDeclineDialogOpen} onOpenChange={setIsDeclineDialogOpen}>
        <DialogContent
          role="alertdialog"
          aria-describedby="decline-descriere"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            cancelButtonRef.current?.focus();
          }}
          className="[&>button]:hidden"
        >
          <DialogTitle>Școala nu participă în {editionName}?</DialogTitle>
          <div id="decline-descriere" className="text-medium">
            <p>Înregistrăm că școala dumneavoastră nu participă în această ediție.</p>
            <ul className="list-disc space-y-1 pl-5">
              <li className="font-semibold">Școala va putea fi reînscrisă de alt profesor.</li>
              <li>Dacă vă răzgândiți, reluați înscrierea de la început, iar cererea așteaptă din nou aprobarea echipei ECOTIC.</li>
            </ul>
          </div>
          <DialogFooter className="gap-3">
            <Button
              ref={cancelButtonRef}
              type="button"
              variant="outline"
              className="touch-target w-full sm:w-auto"
              onClick={() => setIsDeclineDialogOpen(false)}
            >
              Înapoi
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="touch-target w-full sm:w-auto"
              disabled={isSubmitting}
              onClick={handleConfirmDecline}
            >
              Da, școala nu participă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
